import { Box, Card, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import { RT } from '../theme/retentionTheme';

// Small building blocks for the retention screens. Keep screens terse: an icon + a short label says more
// than a sentence.

export const Ico = ({ icon, size = 20, color, sx }) => (
  <Box component={Icon} icon={icon} width={size} height={size} sx={{ color, flexShrink: 0, ...sx }} />
);

// Rounded square with a tinted background and a coloured icon.
export const IconBadge = ({ icon, color = RT.primary, size = 40, iconSize, solid = false, sx }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: size >= 44 ? 3.5 : 2.5,
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
      color: solid ? '#fff' : color,
      background: solid ? color : alpha(color, 0.12),
      ...sx,
    }}
  >
    <Icon icon={icon} width={iconSize || Math.round(size * 0.55)} height={iconSize || Math.round(size * 0.55)} />
  </Box>
);

// Tinted chip in any colour (MUI chips only take palette names).
export const SoftChip = ({ label, color = RT.primary, icon, size = 'small', sx, ...rest }) => (
  <Chip
    size={size}
    label={label}
    icon={icon ? <Icon icon={icon} width={14} height={14} /> : undefined}
    sx={{
      bgcolor: alpha(color, 0.12),
      color,
      '& .MuiChip-icon': { color, ml: '6px', mr: '-4px' },
      ...sx,
    }}
    {...rest}
  />
);

// Card with an icon header, optional right-side action and body.
export const SectionCard = ({ icon, color = RT.primary, title, action, children, sx, bodySx }) => (
  <Card sx={sx}>
    {(title || action) && (
      <Stack direction="row" alignItems="center" gap={1.25} sx={{ px: 2.5, pt: 2.25, pb: 0.5 }}>
        {icon && <IconBadge icon={icon} color={color} size={32} />}
        <Typography variant="subtitle1" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {title}
        </Typography>
        {action}
      </Stack>
    )}
    <Box sx={{ p: 2.5, pt: 1.75, ...bodySx }}>{children}</Box>
  </Card>
);

// KPI / quick-stat tile: icon, big number, short label.
export const StatTile = ({ icon, label, value, color = RT.primary, hint }) => (
  <Card sx={{ flex: 1, minWidth: 160, transition: 'box-shadow .2s, transform .2s', '&:hover': { boxShadow: RT.shadowHover, transform: 'translateY(-1px)' } }}>
    <Stack direction="row" alignItems="center" gap={1.5} sx={{ p: 2 }}>
      <IconBadge icon={icon} color={color} size={44} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15, color: RT.ink, fontVariantNumeric: 'tabular-nums' }}>
          {value ?? '—'}
        </Typography>
        <Typography variant="caption" sx={{ color: RT.muted, fontWeight: 600 }} noWrap>
          {label}
        </Typography>
        {hint && (
          <Typography variant="caption" component="div" sx={{ color: RT.faint }} noWrap>
            {hint}
          </Typography>
        )}
      </Box>
    </Stack>
  </Card>
);

// Icon + one short line. Used whenever an endpoint isn't built yet or a list is empty.
export const EmptyState = ({ icon = 'solar:inbox-line-bold-duotone', title, caption, color = RT.faint, compact }) => (
  <Stack alignItems="center" justifyContent="center" gap={0.75} sx={{ py: compact ? 2.5 : 4, textAlign: 'center' }}>
    <Ico icon={icon} size={compact ? 30 : 40} color={color} />
    <Typography variant="subtitle2" sx={{ color: RT.ink2 }}>
      {title}
    </Typography>
    {caption && (
      <Typography variant="caption" sx={{ color: RT.faint, fontFamily: 'monospace' }}>
        {caption}
      </Typography>
    )}
  </Stack>
);

// Icon · label · value rows.
export const InfoList = ({ rows }) => (
  <Stack divider={<Box sx={{ borderTop: `1px dashed ${RT.line}` }} />}>
    {rows.map(({ icon, label, value }) => (
      <Stack key={label} direction="row" alignItems="center" gap={1.25} sx={{ py: 1.1 }}>
        {icon && <Ico icon={icon} size={18} color={RT.faint} />}
        <Typography variant="body2" sx={{ color: RT.muted, flex: 1 }}>
          {label}
        </Typography>
        <Box sx={{ typography: 'body2', fontWeight: 700, color: RT.ink, textAlign: 'right' }}>{value ?? '—'}</Box>
      </Stack>
    ))}
  </Stack>
);
